import React, { useEffect, useState } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Avatar, Chip,
  CircularProgress, Alert, Divider, Button, List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { roleLabel, roleBadgeColor, capabilitiesForRole } from '../utils/adminRbac';

const TEAL = '#2D6E6A';

interface AdminProfile {
  displayName?: string;
  email?: string;
  username?: string;
  role?: string;
  createdAt?: string;
}

const AdminProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/users/profile');
        const data = await res.json();
        if (cancelled) return;
        if (data.success && data.profile) {
          setProfile(data.profile);
        } else {
          setError(data.error || 'Could not load your profile');
        }
      } catch {
        if (!cancelled) setError('Could not load your profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const role = profile?.role || '';
  const { granted, withheld } = capabilitiesForRole(role);
  const initials = (profile?.displayName || profile?.email || '?')
    .split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F5F8F7', py: { xs: 3, md: 5 } }}>
      <Container maxWidth="md">
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin')}
          sx={{ color: TEAL, textTransform: 'none', mb: 2 }}>
          Back to dashboard
        </Button>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: TEAL }} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            {/* Identity */}
            <Card variant="outlined" sx={{ borderRadius: 3, mb: 3, borderColor: '#E0ECEA' }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
                  <Avatar sx={{ bgcolor: TEAL, width: 64, height: 64, fontSize: 24, fontWeight: 700 }}>
                    {initials}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h5" fontWeight={700} sx={{ color: '#173A37' }}>
                      {profile?.displayName || profile?.username || 'Admin'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">{profile?.email}</Typography>
                    <Chip
                      label={roleLabel(role)}
                      color={roleBadgeColor(role)}
                      size="small"
                      sx={{ mt: 1, fontWeight: 700 }}
                    />
                  </Box>
                </Box>
                {profile?.createdAt && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                    Admin since {new Date(profile.createdAt).toLocaleDateString()}
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Your access */}
            <Card variant="outlined" sx={{ borderRadius: 3, mb: 3, borderColor: '#E0ECEA' }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#173A37' }}>
                  Your access
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  What your <strong>{roleLabel(role)}</strong> role is permitted to do across the admin platform.
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 2, sm: 3 } }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: TEAL, mb: 0.5 }}>
                      Permitted ({granted.length})
                    </Typography>
                    <List dense disablePadding>
                      {granted.map((a) => (
                        <ListItem key={a} disableGutters sx={{ py: 0.25 }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckCircleIcon sx={{ fontSize: 18, color: TEAL }} />
                          </ListItemIcon>
                          <ListItemText primary={a} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'text.disabled', mb: 0.5 }}>
                      Not available ({withheld.length})
                    </Typography>
                    <List dense disablePadding>
                      {withheld.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ pl: 0.5 }}>
                          Full access — no restrictions.
                        </Typography>
                      ) : withheld.map((a) => (
                        <ListItem key={a} disableGutters sx={{ py: 0.25 }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <RemoveCircleOutlineIcon sx={{ fontSize: 18, color: '#B0BEC5' }} />
                          </ListItemIcon>
                          <ListItemText primary={a}
                            primaryTypographyProps={{ variant: 'body2', sx: { color: 'text.disabled' } }} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary">
                  This reflects documented role policy. Actual enforcement happens server-side on every request.
                </Typography>
              </CardContent>
            </Card>


          </>
        )}
      </Container>
    </Box>
  );
};

export default AdminProfilePage;